import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Lock,
  Mail,
} from 'lucide-react'
import { AuthLayout } from '../../components/layout/AuthLayout'
import { GoogleSignInButton } from '../../components/GoogleSignInButton'
import { AccountAvatar } from '../../components/AccountAvatar'
import {
  TurnstileWidget,
  useTurnstileConfig,
} from '../../components/TurnstileWidget'
import { useAuthStore } from '../../stores/authStore'
import {
  scopeForUser,
  useMaintenanceStore,
} from '../../stores/maintenanceStore'
import { api, extractApiError } from '../../lib/api'
import { landingFor } from '../../lib/landing'
import { emailSchema } from '../../lib/validators'

// Sign-in uses the same shared email validator as every other form
// but with the login-specific 40-char cap + "Invalid Email ID."
// rejection text. The password field stays as a "required-only"
// check because the strong-password rule is enforced server-side
// (and we don't want to reject existing valid passwords that pre-
// date the new policy).
const schema = z.object({
  // "Invalid Email ID." named the problem but not the remedy — QA-078 asks
  // validation copy to be actionable, and a bare "Invalid" leaves the user to
  // guess what is wrong with what they typed. Still distinct from the sign-up
  // wording ("Please enter a valid Email ID."), which is why the override
  // exists; it just now says what a valid one looks like.
  emailId: emailSchema({
    max: 40,
    invalidMessage: 'Enter a valid Email ID, like name@company.com.',
  }),
  password: z
    .string({ message: 'Password is required.' })
    .min(1, 'Password is required.'),
  rememberMe: z.boolean(),
})

/**
 * Validate a return-to path passed via `location.state.from` (set by
 * `ProtectedRoute` when an unauthenticated user gets bounced to
 * sign-in). We must NEVER trust this blindly — an attacker could
 * craft a sign-in link whose `from` state points at an attacker
 * site, a different role's surface, or one of our own auth pages
 * (which would create a redirect loop).
 *
 * Returns the path if safe, otherwise `null` (callers fall back to
 * `landingFor(role)`).
 *
 * Safety rules:
 *   1. Must be a non-empty string starting with a single `/`. This
 *      excludes protocol-relative URLs (`//evil.com/...`) and any
 *      absolute URL with a scheme.
 *   2. Must not match an auth-shell route — redirecting back to
 *      `/sign-in` after signing in would loop.
 *   3. Must match the signed-in user's role surface:
 *        - `user`          → must start with `/app/` (or be `/app`)
 *        - `super_admin`   → must start with `/super-admin/` (or be
 *                            `/super-admin`)
 *        - `company_admin` → any flat URL that ISN'T `/app/...` or
 *                            `/super-admin/...` (the flat surface is
 *                            large and adding routes shouldn't break
 *                            return-to silently, so we exclude the
 *                            other roles' prefixes rather than
 *                            enumerate every admin route)
 */
function safeReturnTo(from, role) {
  if (typeof from !== 'string' || from.length === 0) return null
  // Reject protocol-relative (`//host/...`) and absolute URLs.
  if (!from.startsWith('/') || from.startsWith('//')) return null
  // Reject anything that looks like it embeds a host or scheme.
  if (from.includes('\\') || from.includes('://')) return null

  // Don't bounce the user back into the auth shell after sign-in.
  const authPaths = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password']
  if (from === '/' || authPaths.some((p) => from === p || from.startsWith(p + '/'))) {
    return null
  }

  const isAppPath = from === '/app' || from.startsWith('/app/')
  const isSuperPath = from === '/super-admin' || from.startsWith('/super-admin/')

  if (role === 'user') {
    return isAppPath ? from : null
  }
  if (role === 'super_admin') {
    return isSuperPath ? from : null
  }
  if (role === 'company_admin') {
    // Company admins own the flat URL space AND share the `/app/` surface —
    // spaces, lists, boards and documents all live there and a company admin
    // reaches them routinely. Rejecting `/app/` here meant deep-linking to a
    // list while signed out dropped the destination and landed the admin on
    // their dashboard instead (QA-118). Only another ROLE's surface is
    // refused, which for them is `/super-admin/`.
    return !isSuperPath ? from : null
  }
  return null
}

// Confirm a successful sign-in. Dismiss any lingering toast first — notably a
// "Signed out successfully." from the previous session that can still be
// on-screen (sonner's Toaster survives the SPA navigation), so only the
// sign-in confirmation shows, never both at once.
function confirmSignedIn() {
  toast.dismiss()
  toast.success('Signed in successfully.')
}

export function SignInPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, adoptGoogleSession, rememberMe, rememberedEmail, status, user } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  // Account picker — non-null (an array of accounts) when one Email ID +
  // password matched several companies and the user must choose which to
  // sign into. `blockedChoices` lists memberships that CAN'T sign in
  // (suspended / organisation unavailable) so the person sees why that
  // company is missing instead of being silently dropped into another.
  const [accountChoices, setAccountChoices] = useState(null)
  const [blockedChoices, setBlockedChoices] = useState([])
  // True while the Google multi-company picker is showing — hides the
  // email/password form (Google has already authenticated the user, so only
  // the company choice should remain).
  const [googlePicking, setGooglePicking] = useState(false)
  const turnstile = useTurnstileConfig()
  const [turnstileToken, setTurnstileToken] = useState('')
  // "Add account" mode (account switcher): the user is already signed in
  // and wants to authenticate an ADDITIONAL account on this device. We
  // must NOT auto-redirect them away from the form in this case.
  const addMode = new URLSearchParams(location.search).get('add') === '1'

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      // Don't prefill the remembered email when adding a second account —
      // they're signing into a DIFFERENT one.
      emailId: addMode ? '' : rememberMe ? rememberedEmail : '',
      password: '',
      rememberMe,
    },
  })

  useEffect(() => {
    // In add-account mode we deliberately stay on the form despite an
    // active session; navigation after a successful add is handled in
    // onSubmit instead.
    if (addMode) return
    if (status === 'authenticated' && user) {
      // `ProtectedRoute` stashes the originally-requested path in
      // `location.state.from` before redirecting unauthenticated
      // users here. Prefer that over the default landing page so the
      // user lands where they tried to go — but only if the path is
      // safe (see `safeReturnTo` for the rules).
      const from = location.state?.from
      const target = safeReturnTo(from, user.role) ?? landingFor(user.role)
      navigate(target, { replace: true })
    }
  }, [status, user, navigate, location.state, addMode])

  async function onSubmit(values) {
    if (turnstile.enabled && !turnstileToken) {
      toast.error("Please complete the 'I'm not a robot' verification.")
      return
    }
    setSubmitting(true)
    try {
      const result = await signIn(
        values.emailId.trim(),
        values.password,
        values.rememberMe,
        turnstileToken,
      )
      if (result?.multipleAccounts) {
        // Several companies share this Email ID + password — show the
        // picker. The credentials stay in the form so the chosen-company
        // re-submit can reuse them.
        setAccountChoices(result.accounts)
        setBlockedChoices(result.blockedAccounts ?? [])
        return
      }
      const u = useAuthStore.getState().user
      // If this user's panel is in maintenance, the gate bounces them to
      // /maintenance (which shows its own warning toast) — suppress the
      // success toast there so the two don't contradict each other. Show
      // it only when the panel is actually live.
      if (!useMaintenanceStore.getState().isScopeDown(scopeForUser(u))) {
        confirmSignedIn()
      }
      if (addMode) {
        // The new account is now the active session — send the user to
        // its role landing. The redirect effect is disabled in add-mode,
        // so navigate explicitly here.
        navigate(landingFor(u?.role), { replace: true })
      }
    } catch (err) {
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          form.setError(name, { message: msg })
          mapped = true
        }
      }
      if (!mapped) toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  // Account picker — re-submit sign-in scoped to the chosen company. The
  // credentials are still in the form (the picker doesn't clear them), so
  // we reuse them with the selected tenantId; the server re-verifies the
  // password and skips the already-consumed Turnstile token.
  async function chooseAccount(tenantId) {
    const v = form.getValues()
    setSubmitting(true)
    try {
      const result = await signIn(
        v.emailId.trim(),
        v.password,
        v.rememberMe,
        turnstileToken,
        tenantId,
      )
      if (result?.multipleAccounts) {
        setAccountChoices(result.accounts)
        setBlockedChoices(result.blockedAccounts ?? [])
        return
      }
      const u = useAuthStore.getState().user
      if (!useMaintenanceStore.getState().isScopeDown(scopeForUser(u))) {
        confirmSignedIn()
      }
      if (addMode) {
        navigate(landingFor(u?.role), { replace: true })
      }
    } catch (err) {
      toast.error(extractApiError(err).message)
    } finally {
      setSubmitting(false)
    }
  }

  // Google sign-in completes via the button's onSuccess callback (not
  // onSubmit), and the auto-redirect effect is disabled in add-account
  // mode — so when ADDING an account via Google we must navigate
  // explicitly here too, exactly like the email/password path above.
  // Outside add-mode this just adopts the session and lets the redirect
  // effect route the user as before.
  function handleGoogleSuccess(payload) {
    adoptGoogleSession(payload)
    const u = useAuthStore.getState().user
    // Mirror the email/password path: confirm the sign-in with a success
    // toast unless this user's panel is in maintenance (the /maintenance
    // screen shows its own warning toast, which would otherwise contradict
    // a success message).
    if (!useMaintenanceStore.getState().isScopeDown(scopeForUser(u))) {
      confirmSignedIn()
    }
    if (addMode) {
      navigate(landingFor(u?.role), { replace: true })
    }
  }

  const emailError = form.formState.errors.emailId?.message
  const passwordError = form.formState.errors.password?.message

  return (
    <AuthLayout
      centered
      title={
        accountChoices
          ? 'Choose an account'
          : addMode
            ? 'Add another account'
            : 'Welcome Back'
      }
      subtitle={
        accountChoices
          ? 'This Email ID is registered to more than one company. Choose which account to sign in to.'
          : addMode
            ? 'Sign In to another account to switch between them.'
            : 'Sign In to continue to TaskMagix'
      }
    >
      {accountChoices ? (
        <AccountPicker
          accounts={accountChoices}
          blockedAccounts={blockedChoices}
          busy={submitting}
          onChoose={chooseAccount}
          onBack={() => {
            setAccountChoices(null)
            setBlockedChoices([])
          }}
        />
      ) : (
        <>
          {/* Google SSO button — self-hides when the Super Admin hasn't
           *  enabled Google SSO, so we can render it unconditionally here
           *  and the visible layout adapts to whatever the platform is
           *  configured for. The "OR" divider is rendered conditionally
           *  by the GoogleSsoDivider wrapper below so we don't show a
           *  dangling divider above an empty space. */}
          <GoogleSsoBlock
            onSuccess={handleGoogleSuccess}
            mode="signin"
            onPickerChange={setGooglePicking}
          />

      {googlePicking ? null : (
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="space-y-5"
      >
        {/* Email field — icon prefix inside the input. Same pattern
         *  is used by Linear / Stripe sign-in forms: a 14-16px glyph
         *  pinned to the left edge with input padded right of it. */}
        <div>
          <label
            htmlFor="emailId"
            className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
          >
            Email ID
            <span
              aria-hidden="true"
              className="ml-0.5 text-[color:var(--color-priority-urgent)]"
            >
              *
            </span>
          </label>
          <div className="relative">
            <Mail
              size={16}
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
            />
            <input
              id="emailId"
              type="email"
              autoComplete="email"
              placeholder="Enter the Email ID"
              // This page hand-rolls its inputs rather than using the shared
              // `TextField`, so it must repeat the same a11y wiring that
              // component already does: the error is announced as the field's
              // STATE (aria-invalid) and pointed at by `aria-describedby`, so a
              // screen reader reads "Email ID, required, invalid entry, Email
              // ID is required." instead of never mentioning the failure.
              aria-invalid={Boolean(emailError)}
              aria-required="true"
              aria-describedby={emailError ? 'emailId-error' : undefined}
              className={
                'w-full h-11 pl-10 pr-3 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
                (emailError
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              {...form.register('emailId')}
            />
          </div>
          {emailError ? (
            <p
              id="emailId-error"
              role="alert"
              className="mt-1 text-xs text-[color:var(--color-danger-text)]"
            >
              {emailError}
            </p>
          ) : null}
        </div>

        {/* Password field — Lock icon prefix + eye toggle suffix. */}
        <div>
          <label
            htmlFor="password"
            className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
          >
            Password
            <span
              aria-hidden="true"
              className="ml-0.5 text-[color:var(--color-priority-urgent)]"
            >
              *
            </span>
          </label>
          <div className="relative">
            <Lock
              size={16}
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter the Password"
              maxLength={128}
              aria-invalid={Boolean(passwordError)}
              aria-required="true"
              aria-describedby={passwordError ? 'password-error' : undefined}
              className={
                'w-full h-11 pl-10 pr-10 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
                (passwordError
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </div>
          {passwordError ? (
            <p
              id="password-error"
              role="alert"
              className="mt-1 text-xs text-[color:var(--color-danger-text)]"
            >
              {passwordError}
            </p>
          ) : null}
        </div>

        {/* Remember me + Forgot password row. */}
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer select-none text-[color:var(--color-ink)]">
            <span className="relative inline-flex h-4 w-4 shrink-0">
              <input
                type="checkbox"
                className="peer absolute inset-0 opacity-0 cursor-pointer"
                {...form.register('rememberMe')}
              />
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-[4px] border border-[color:var(--color-ink-muted)] bg-[color:var(--color-card)] transition-colors peer-hover:border-[color:var(--color-ink)] peer-checked:bg-[color:var(--color-primary)] peer-checked:border-[color:var(--color-primary)] pointer-events-none"
              />
              <Check
                size={11}
                strokeWidth={3}
                aria-hidden="true"
                className="absolute inset-0 m-auto text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
              />
            </span>
            Remember me
          </label>
          <Link
            to="/forgot-password"
            // `inline-flex` + `min-h-[24px]` (24px) meets WCAG 2.2 SC 2.5.8 Target
            // Size (Minimum). The bare link was 111x20 at 360px width, under
            // the 24px floor. The negative inline margin keeps the TEXT
            // optically aligned with the "Remember me" row while the hit area
            // grows, so the layout is unchanged.
            className="inline-flex items-center min-h-[24px] -my-1 px-1 -mx-1 text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)] hover:underline underline-offset-2 transition-colors font-medium"
          >
            Forgot password?
          </Link>
        </div>

        {/* Cloudflare Turnstile — only renders when the platform has
         *  bot protection switched on. Submit stays disabled until the
         *  challenge produces a token. */}
        {turnstile.enabled && turnstile.siteKey ? (
          <TurnstileWidget
            siteKey={turnstile.siteKey}
            onToken={setTurnstileToken}
          />
        ) : null}

        {/* Primary Sign In CTA — full width, arrow icon, brand
         *  navy → ocean gradient. */}
        <button
          type="submit"
          disabled={submitting || (turnstile.enabled && !turnstileToken)}
          className="group w-full h-12 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 shadow-[0_8px_20px_-6px_rgba(2,54,123,0.35)] hover:shadow-[0_12px_28px_-6px_rgba(2,54,123,0.45)] transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          style={{
            background:
              'linear-gradient(135deg, #02367B 0%, #006CA5 50%, #0496C7 100%)',
          }}
        >
          {submitting ? (
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : null}
          Sign In
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>

        {/* Bottom signup link. */}
        <p className="text-center text-sm text-[color:var(--color-ink-muted)]">
          New to TaskMagix?{' '}
          <Link
            to="/sign-up"
            // Same 24px minimum target as the Forgot-password link above; this
            // one measured 115x16 at 360px. `align-middle` keeps it sitting on
            // the same baseline as the "New to TaskMagix?" text around it.
            className="inline-flex items-center align-middle min-h-[24px] px-1 -mx-1 font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)] hover:underline underline-offset-2 transition-colors"
          >
            Create an account
          </Link>
        </p>

      </form>
      )}
        </>
      )}
    </AuthLayout>
  )
}

function pickerRoleText(acc) {
  if (acc.role === 'super_admin') return 'Master Admin'
  if (acc.role === 'company_admin') return 'Company Admin'
  if (acc.role === 'sub_admin') return 'Sub Admin'
  return acc.workRoleName || 'User'
}

/** Account picker — shown when one Email ID + password matches accounts in
 *  several companies. Choosing a company re-submits the sign-in scoped to
 *  that tenant; the password is re-verified server-side. */
function AccountPicker({ accounts, blockedAccounts = [], busy, onChoose, onBack }) {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {accounts.map((acc) => (
          <button
            key={acc.userId}
            type="button"
            disabled={busy}
            onClick={() => onChoose(acc.tenantId)}
            className="w-full flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-3 text-left transition-colors hover:border-[color:var(--color-primary)] disabled:opacity-60 disabled:cursor-wait cursor-pointer"
          >
            <AccountAvatar name={acc.tenantName} size={40} />
            <span className="min-w-0 flex-1">
              {/* The Email ID is the same on every card (named in the
               *  subtitle above), so each card leads with its COMPANY and
               *  the person's role there. */}
              <span className="block truncate font-semibold text-[color:var(--color-ink-strong)]">
                {acc.tenantName || acc.fullName || acc.emailId}
              </span>
              <span className="block truncate text-sm text-[color:var(--color-ink-muted)]">
                {pickerRoleText(acc)}
              </span>
            </span>
            <ArrowRight
              size={16}
              className="shrink-0 text-[color:var(--color-ink-muted)]"
            />
          </button>
        ))}
      </div>

      {/* Memberships that exist but CAN'T sign in right now (suspended
       *  account, unavailable organisation). Shown so the person knows
       *  why that company is missing — previously these were silently
       *  dropped, which read as "logged me in as someone else" (QA). */}
      {blockedAccounts.length > 0 ? (
        <div className="space-y-2">
          {blockedAccounts.map((acc) => (
            <div
              key={acc.userId}
              aria-disabled="true"
              title={acc.reason ?? ''}
              className="w-full flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-3 text-left opacity-60 cursor-not-allowed"
            >
              <AccountAvatar name={acc.tenantName} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-[color:var(--color-ink-strong)]">
                  {acc.tenantName || acc.fullName}
                </span>
                <span className="block truncate text-sm text-[color:var(--color-priority-urgent)]">
                  {acc.status === 'organisation_unavailable'
                    ? 'Company unavailable'
                    : acc.status === 'invited'
                      ? 'Invitation pending'
                      : acc.status === 'suspended'
                        ? 'Account suspended'
                        : 'Account unavailable'}
                  {acc.reason ? ` — ${acc.reason}` : ''}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onBack}
        disabled={busy}
        className="text-sm font-medium text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)] disabled:opacity-60 cursor-pointer"
      >
        ← Use a different Email ID
      </button>
    </div>
  )
}

/** Conditionally renders the Google button + "OR sign in with email"
 *  divider. The button itself self-hides when Google SSO is disabled;
 *  we use the same flag to also hide the surrounding chrome so the
 *  email/password form isn't preceded by a dangling divider. The flag
 *  is fetched by the inner button and surfaced via a render callback
 *  so we don't make a second HTTP call. */
function GoogleSsoBlock({ onSuccess, mode, onPickerChange }) {
  const [visible, setVisible] = useState(false)
  const [picking, setPicking] = useState(false)
  // Probe the config endpoint once — the GoogleSignInButton does this
  // independently to render itself, but we need the same signal here
  // to decide whether to render the divider. Cheap (one GET, cached
  // by the browser).
  useEffect(() => {
    let cancelled = false
    api
      .get('/auth/google/config')
      .then((res) => {
        if (!cancelled) setVisible(Boolean(res.data?.enabled && res.data?.clientId))
      })
      .catch(() => {
        if (!cancelled) setVisible(false)
      })
    return () => {
      cancelled = true
    }
  }, [])
  if (!visible) return null
  return (
    <div className="mb-4">
      <GoogleSignInButton
        mode={mode}
        onSuccess={onSuccess}
        onPickerChange={(active) => {
          setPicking(active)
          onPickerChange?.(active)
        }}
      />
      {/* Divider hides once the Google company-picker is showing — after
          Google auth the email/password path is gone, so a dangling
          "Or sign in with email" would be misleading. */}
      {picking ? null : (
        <div className="flex items-center gap-3 mt-4 text-[11px] uppercase tracking-[0.12em] font-semibold text-[color:var(--color-ink-subtle)]">
          <div className="flex-1 h-px bg-[color:var(--color-rule)]" />
          Or sign in with email
          <div className="flex-1 h-px bg-[color:var(--color-rule)]" />
        </div>
      )}
    </div>
  )
}

